/* global isBrowser, API_ROOT */
import { camelizeKeys, decamelizeKeys } from 'humps'

// In-flight request deduplication for GET requests
const inFlightRequests = new Map()

const deduplicatedFetch = (url, options) => {
  const method = options?.method || 'GET'
  if (method !== 'GET') return fetch(url, options)

  const key = `GET:${url}`
  let promise
  if (inFlightRequests.has(key)) promise = inFlightRequests.get(key)
  else {
    promise = fetch(url, options).finally(() => {
      inFlightRequests.delete(key)
    })
    inFlightRequests.set(key, promise)
  }
  // Hand each caller its own clone — the underlying Response body is a
  // single-use stream, so concurrent callers reading the same Response
  // would race and the loser would see an empty body.
  return promise.then((response) => response.clone())
}

// Get auth token from state
const getAuthToken = (state) => state?.auth?.token || ''

export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;`
}

export const getUrl = (path, filters) => {
  let query = ''
  if (filters) {
    query = Object.entries(filters)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    query = query ? `?${query}` : ''
  }
  return `${path}${query}`
}

export const fetchHelper =
  (url, options, success, failure, config = {}) =>
  (body = {}) =>
  async (dispatch, getState) => {
    const fetchingAction = { type: options?.hidden ? 'HIDDEN_FETCHING' : 'FETCHING' }
    dispatch(fetchingAction)

    const extraOptions = { credentials: 'include' }

    if (Object.keys(body).length) {
      const split = config.decamelizeNumbers ? /(?=[A-Z0-9])/ : /(?=[A-Z])/
      extraOptions.body = JSON.stringify(decamelizeKeys(body, { split }))
    }

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options?.headers
    }

    // Add auth token if available
    const token = getAuthToken(getState())
    if (token) headers.Authorization = `JWT ${token}`

    try {
      const response = await deduplicatedFetch(url, {
        ...options,
        ...extraOptions,
        headers
      })

      const value = await response.json().catch(() => ({}))

      if (!response.ok) {
        const failurePayload = {
          value,
          status: response.status,
          ...(config.requestBodyOnFailure && { requestBody: body })
        }
        const failureAction = failure(failurePayload)
        dispatch(failureAction)
        return [fetchingAction, failureAction]
      }

      const successPayload = {
        ...camelizeKeys(value),
        ...(config.requestBodyOnSuccess && { requestBody: body })
      }
      const successAction = success(successPayload)
      dispatch(successAction)
      return [fetchingAction, successAction]
    } catch (error) {
      const failurePayload = {
        value: { error: error.message },
        status: 500,
        ...(config.requestBodyOnFailure && { requestBody: body })
      }
      const failureAction = failure(failurePayload)
      dispatch(failureAction)
      return [fetchingAction, failureAction]
    }
  }

export default (endpoint, options, success, failure, config) =>
  fetchHelper(`${API_ROOT}${endpoint}`, options, success, failure, config)
